import { Button, Spinner } from '@cartwheel/components'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { ClinicalQualityCheckerButton } from './clinical-quality-checker-button'
import { qualityScoreReportPath } from './clinical-quality-score-display'
import { ClinicalQualityScoreHistoryModal } from './clinical-quality-score-history-modal'

export interface LatestScoreSummary {
  id: string
  percentage: number
  recommendation: string | null
  has_escalation_flags: boolean
  created_at: string
}

interface Props {
  chartNoteId: string
  clientId: string
  latestScore: LatestScoreSummary | null
  scoringInProgress?: boolean
  onScoreComplete: (score: LatestScoreSummary) => void
}

export function ClinicalQualityScoreRowActions({
  chartNoteId,
  clientId,
  latestScore,
  scoringInProgress = false,
  onScoreComplete,
}: Props) {
  const [expanded, setExpanded] = useState(scoringInProgress)
  const [pendingRun, setPendingRun] = useState(false)
  const [isRunning, setIsRunning] = useState(scoringInProgress)
  const [scoringError, setScoringError] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  const handleRunCheck = () => {
    setScoringError(null)
    setExpanded(true)
    setPendingRun(true)
  }

  return (
    <>
      {scoringError ? (
        <div className="flex items-center gap-2 justify-end">
          <span className="text-xs text-orange">{scoringError}</span>
          <Button variant="tertiary" onClick={handleRunCheck}>
            Retry
          </Button>
        </div>
      ) : latestScore ? (
        isRunning ? (
          <Button variant="secondary" disabled>
            <Spinner size="sm" />
            Scoring…
          </Button>
        ) : (
          <div className="flex items-center gap-2 justify-end">
            <a
              href={qualityScoreReportPath(latestScore.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo hover:underline text-sm whitespace-nowrap"
            >
              <i
                className="fa-solid fa-arrow-up-right-from-square mr-1.5 text-xs"
                aria-hidden="true"
              />
              View result
            </a>
            <Button variant="text" onClick={() => setHistoryOpen(true)}>
              History
            </Button>
            <Button variant="tertiary" onClick={handleRunCheck}>
              Re-run
            </Button>
          </div>
        )
      ) : isRunning ? (
        <Button variant="secondary" disabled>
          <Spinner size="sm" />
          Scoring…
        </Button>
      ) : (
        <Button variant="secondary" onClick={handleRunCheck}>
          <i className="fa-solid fa-clipboard-check mr-1.5" aria-hidden="true" />
          Run Clinical Quality Check
        </Button>
      )}

      {historyOpen && (
        <ClinicalQualityScoreHistoryModal
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          chartNoteId={chartNoteId}
          clientId={clientId}
        />
      )}

      {expanded &&
        createPortal(
          <ClinicalQualityCheckerButton
            chartNoteId={chartNoteId}
            clientId={clientId}
            autoRun={pendingRun}
            resume={scoringInProgress && !pendingRun}
            onRunStarted={() => setPendingRun(false)}
            onStatusChange={(s) => {
              const running = s === 'submitting' || s === 'polling'
              setIsRunning(running)
              if (!running) {
                setExpanded(false)
              }
            }}
            onComplete={(score) => {
              onScoreComplete({
                id: score.id,
                percentage: score.percentage,
                recommendation: score.recommendation,
                has_escalation_flags: score.has_escalation_flags,
                created_at: score.created_at,
              })
              setExpanded(false)
              setIsRunning(false)
            }}
            onError={(message: string) => {
              setScoringError(message)
              setExpanded(false)
              setIsRunning(false)
            }}
          />,
          document.body
        )}
    </>
  )
}
